/**
 * ActionPreviewEvent — renderiza tipo 'action_preview'
 * payload: { action_id, resumen, diff, reversibilidad, requiere_aprobacion }
 *
 * R-PT-11: muestra preview/diff y captura la aprobación EXPLÍCITA del usuario.
 *          Pórtico NO infiere ni presupone la aprobación.
 *          La aprobación va a POST /v1/actions/{id}/approve (Bearer, sin body).
 *
 * Máquina de estados (SPEC #280 §B.3): propuesta → aprobando → aprobada
 * | descartada (client-only, §B.5, sin llamada al kernel) | error (§B.4).
 * A `error` se llega por dos caminos, y el segundo es V-6b: un error del kernel
 * (§B.4) o un 200 cuya proyección NO viene en `ejecutada` — el kernel responde
 * 200 también cuando se niega a escribir, y eso no es un éxito.
 * `diff` es tolerante: hoy llega como string (mock) u objeto {antes,despues}
 * (as-built kernel); la forma definitiva la cierra Cassie (#281) — no se
 * asume ninguna de las dos.
 */

import { useState } from 'react'
import { Wrench, RotateCcw, OctagonAlert } from 'lucide-react'
import { approveAction } from '../../api/client.js'
import { useSession } from '../../context/SessionContext.jsx'

const REVERSIBILIDAD_LABELS = {
  reversible:   { label: 'Reversible',   cls: 'rev-badge--safe',    Icon: RotateCcw },
  irreversible: { label: 'Irreversible', cls: 'rev-badge--danger',  Icon: OctagonAlert },
}

// SPEC §B.4 — mapa de errores del kernel (envelope {error:{code,message}}) a
// mensaje de UI. `retryable`: si es recuperable, la tarjeta vuelve a
// "propuesta" (re-habilita Aprobar); si no, queda en estado terminal "error".
const ERROR_MAP = {
  forbidden:              { message: 'No tienes permiso para aprobar esta acción.', retryable: false },
  accion_no_encontrada:   { message: 'La acción ya no está disponible (expiró o no existe).', retryable: false },
  en_curso:               { message: 'La acción se está ejecutando…', retryable: false },
  accion_estado_terminal: { message: 'La acción ya finalizó o expiró.', retryable: false },
}

function errorInfoFor(err) {
  const known = ERROR_MAP[err?.code]
  if (known) return known
  return { message: err?.message || 'No se pudo aprobar la acción.', retryable: true }
}

// V-6b — el único estado de la proyección que significa «hecho». El kernel
// responde 200 también cuando A3/A4 RECHAZA la escritura (`fallida_rollback`), y
// un 200 no lanza en el cliente, así que sin esta comprobación la tarjeta pintaba
// «✓ Aprobada — hecho» sobre una escritura que el kernel se negó a hacer.
const EJECUTADA = 'ejecutada'

// Deriva líneas +/- del diff sin asumir su forma (§B.6, pendiente Cassie #281):
// string -> se pinta tal cual; objeto {antes,despues} -> se derivan líneas.
function DiffBlock({ diff }) {
  if (diff == null) return null

  if (typeof diff === 'string') {
    const lines = diff.split('\n').map((line, i) => {
      const cls = line.startsWith('+') ? 'action-preview__diff-line--add'
        : line.startsWith('-') ? 'action-preview__diff-line--del' : undefined
      return <span key={i} className={cls}>{line}{'\n'}</span>
    })
    return <pre className="action-preview__diff">{lines}</pre>
  }

  if (typeof diff === 'object' && ('antes' in diff || 'despues' in diff)) {
    const antes = diff.antes
    const despues = diff.despues
    const lines = []
    const fmt = v => (typeof v === 'object' && v !== null) ? JSON.stringify(v) : String(v ?? '')

    if (antes && typeof antes === 'object' && despues && typeof despues === 'object') {
      const keys = [...new Set([...Object.keys(antes), ...Object.keys(despues)])]
      for (const key of keys) {
        const before = antes[key]
        const after = despues[key]
        if (JSON.stringify(before) === JSON.stringify(after)) {
          lines.push({ text: `  ${key}: ${fmt(before)}` })
        } else {
          if (before !== undefined) lines.push({ text: `- ${key}: ${fmt(before)}`, cls: 'action-preview__diff-line--del' })
          if (after !== undefined) lines.push({ text: `+ ${key}: ${fmt(after)}`, cls: 'action-preview__diff-line--add' })
        }
      }
    } else {
      if (antes !== undefined) lines.push({ text: `- ${fmt(antes)}`, cls: 'action-preview__diff-line--del' })
      if (despues !== undefined) lines.push({ text: `+ ${fmt(despues)}`, cls: 'action-preview__diff-line--add' })
    }

    return (
      <pre className="action-preview__diff">
        {lines.map((l, i) => <span key={i} className={l.cls}>{l.text}{'\n'}</span>)}
      </pre>
    )
  }

  // Forma desconocida: no se inventa estructura, se pinta cruda.
  return <pre className="action-preview__diff">{JSON.stringify(diff, null, 2)}</pre>
}

// GEN-PT-004: estado con el que llega la tarjeta al reabrir el chat (el kernel
// lo lee del registro de la acción). En streaming no viene: nace `propuesta`.
const ESTADO_INICIAL = { aprobada: 'aprobada', caducada: 'caducada', error: 'error', rechazada: 'rechazada' }

// GEN-HU-016: `espera_firma` = el turno del agente está PAUSADO esperando esta
// firma. Aprobar o Rechazar avisan a quien monta la tarjeta (`onDecision`) para
// que reanude el turno; el rechazo, en ese caso, sí llega al kernel (vía la
// reanudación) en vez de ser un descarte local.
export default function ActionPreviewEvent({ payload, onApproved, onDecision }) {
  const { action_id, resumen, diff, reversibilidad, requiere_aprobacion, estado, espera_firma } = payload
  const { capabilities } = useSession()
  const [status, setStatus]     = useState(ESTADO_INICIAL[estado] ?? 'propuesta') // propuesta | aprobando | aprobada | descartada | caducada | error
  const [resultado, setResultado] = useState(null)
  const [errorMsg, setErrorMsg] = useState(
    estado === 'error' ? 'No se aplicó: la acción no consta como ejecutada.' : null,
  )

  const revMeta = REVERSIBILIDAD_LABELS[reversibilidad] ?? {
    label: reversibilidad, cls: 'rev-badge--unknown', Icon: null,
  }
  const RevIcon = revMeta.Icon

  // Gate de capacidad (§B.2): el botón Aprobar solo se pinta si el descriptor
  // concede `aprobar_accion` (DKtec sí; Koana no). El Kernel revalida IAM
  // igualmente (R-PT-04); esto es solo evitar pintar un botón inútil.
  const puedeAprobar = capabilities?.acciones_permitidas?.includes('aprobar_accion') ?? false

  async function handleApprove() {
    setStatus('aprobando')
    setErrorMsg(null)
    try {
      // approveAction ya añade el Bearer en el cliente; no se le pasa token
      // ni ningún payload de negocio (§B.2: acción ya validada por el Kernel).
      const proyeccion = await approveAction(action_id)
      // V-6b — un 200 NO es un éxito. El kernel devuelve 200 con
      // `estado: fallida_rollback` cuando la ejecución se rechaza (p. ej. la
      // revalidación de V-5a: el estado del objeto cambió entre la propuesta y la
      // aprobación, así que NO se escribe). El cliente solo lanza si `!res.ok`, de
      // modo que ese camino llegaba hasta aquí y se pintaba como hecho: el
      // custodio aprobaba, el kernel se negaba a escribir —y hacía bien— y la
      // pantalla le decía que estaba hecho. Un control que dispara mientras la
      // superficie informa de éxito es la puerta pintada con el signo cambiado.
      //
      // Se comprueba en positivo (`=== ejecutada`) y no listando los fallos: un
      // estado que no conocemos no se pinta como hecho. Y va ANTES de
      // `onApproved`, porque ese enganche es el que cierra el preview y recarga el
      // listado — dispararlo sin escritura es afirmar el efecto dos veces.
      if (proyeccion?.estado !== EJECUTADA) {
        setErrorMsg(
          proyeccion?.escalada?.motivo
            ? `No se ha aplicado: ${proyeccion.escalada.motivo}`
            : 'No se ha aplicado: el kernel no ejecutó la acción.',
        )
        setStatus('error')
        return
      }
      // Reintento idempotente: aprobar dos veces devuelve 200 con el
      // resultado ya registrado (no re-ejecuta) — se trata igual que un OK.
      setResultado(proyeccion?.resultado ?? null)
      setStatus('aprobada')
      if (espera_firma) onDecision?.(action_id, 'aprobada')
      // Enganche opcional (#323): quien monta la tarjeta fuera del chat (p. ej.
      // el visor de ficheros para desbloquear la descarga de raw tras aprobar).
      onApproved?.(proyeccion)
    } catch (err) {
      const info = errorInfoFor(err)
      setErrorMsg(info.message)
      setStatus(info.retryable ? 'propuesta' : 'error')
    }
  }

  // Descartar es MVP client-only (§B.5): sin llamada al Kernel. La acción
  // pendiente expira sola en el Kernel a los 15 min y no puede ejecutarse
  // sin aprobación.
  function handleDiscard() {
    if (espera_firma) {
      setStatus('rechazada')
      onDecision?.(action_id, 'rechazada')
      return
    }
    setStatus('descartada')
  }

  return (
    <div className={'event-action-preview action-preview--' + status}>
      <div className="action-preview__header">
        <span className="action-preview__icon"><Wrench size={16} strokeWidth={1.8} aria-hidden="true" /></span>
        <span className="action-preview__title">Acción propuesta</span>
        <span className={'rev-badge ' + revMeta.cls}>
          {RevIcon && <RevIcon size={13} strokeWidth={2} aria-hidden="true" />}{revMeta.label}
        </span>
      </div>

      <p className="action-preview__resumen">{resumen}</p>

      <DiffBlock diff={diff} />

      {status === 'propuesta' && requiere_aprobacion && (
        <div className="action-preview__actions">
          {puedeAprobar ? (
            <>
              <p className="action-preview__notice">
                {espera_firma
                  ? 'El agente está esperando tu firma para continuar. Esta acción requiere tu aprobación explícita antes de ejecutarse.'
                  : 'Esta acción requiere tu aprobación explícita antes de ejecutarse.'}
              </p>
              <div className="action-preview__btns">
                <button
                  className="action-btn action-btn--approve"
                  onClick={handleApprove}
                >
                  ✓ Aprobar
                </button>
                <button
                  className="action-btn action-btn--reject"
                  onClick={handleDiscard}
                >
                  {espera_firma ? '✕ Rechazar' : '✕ Descartar'}
                </button>
              </div>
            </>
          ) : (
            <p className="action-preview__notice">
              Esta acción requiere aprobación de alguien con permiso para ello.
            </p>
          )}
          {errorMsg && <p className="action-preview__error">{errorMsg}</p>}
        </div>
      )}

      {status === 'aprobando' && (
        <p className="action-preview__status">Aprobando…</p>
      )}

      {status === 'aprobada' && (
        <p className="action-preview__status action-preview__status--ok">
          {/* El resultado puede ser un string (chat) o un objeto (p. ej. el
              grant de lectura de raw): solo se pinta inline si es texto. */}
          ✓ Aprobada — hecho{typeof resultado === 'string' ? `: ${resultado}` : ''}
        </p>
      )}

      {status === 'descartada' && (
        <p className="action-preview__status action-preview__status--descartada">
          Descartada — la acción no se ejecutará.
        </p>
      )}

      {status === 'rechazada' && (
        <p className="action-preview__status action-preview__status--descartada">
          Rechazada — la acción no se ejecutó.
        </p>
      )}

      {status === 'caducada' && (
        <p className="action-preview__status action-preview__status--descartada">
          Caducada — la acción no se ejecutó.
        </p>
      )}

      {status === 'error' && (
        <p className="action-preview__status action-preview__status--error">
          {errorMsg}
        </p>
      )}
    </div>
  )
}
